import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

const TimePicker = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const parsed = useMemo(() => {
    if (!value) return { hour12: null, minute: null, period: null };
    const [hStr, mStr] = value.split(":");
    const h24 = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    const period = h24 >= 12 ? "PM" : "AM";
    const hour12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return { hour12, minute: m, period };
  }, [value]);

  const [selHour, setSelHour] = useState(parsed.hour12);
  const [selMinute, setSelMinute] = useState(parsed.minute);
  const [selPeriod, setSelPeriod] = useState(parsed.period);

  useEffect(() => {
    setSelHour(parsed.hour12);
    setSelMinute(parsed.minute);
    setSelPeriod(parsed.period);
  }, [parsed.hour12, parsed.minute, parsed.period]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest("[data-timepicker-dropdown]")) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Position dropdown relative to trigger using portal
  useEffect(() => {
    if (!isOpen || !ref.current) return;

    const updatePosition = () => {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  const emit = (hour12, minute, period) => {
    if (hour12 === null || minute === null || period === null) return;
    const h24 =
      period === "AM"
        ? hour12 === 12 ? 0 : hour12
        : hour12 === 12 ? 12 : hour12 + 12;
    onChange(`${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
  };

  const handleHour = (h) => { setSelHour(h); emit(h, selMinute, selPeriod); };
  const handleMinute = (m) => { setSelMinute(m); emit(selHour, m, selPeriod); };
  const handlePeriod = (p) => { setSelPeriod(p); emit(selHour, selMinute, p); };

  const displayLabel = useMemo(() => {
    if (selHour === null || selMinute === null || selPeriod === null) return null;
    return `${selHour}:${String(selMinute).padStart(2, "0")} ${selPeriod}`;
  }, [selHour, selMinute, selPeriod]);

  const colBase = "flex flex-col overflow-y-auto max-h-[180px] scrollbar-thin";
  const rowBase = "px-4 py-2 text-[14px] cursor-pointer transition-colors text-center select-none";

  const dropdown = isOpen ? (
    <div
      data-timepicker-dropdown
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 999999,
      }}
      className="bg-[#021529] border border-[#192634] rounded-2xl shadow-xl flex overflow-hidden"
    >
      {/* Hours */}
      <div className={`${colBase} flex-1 border-r border-[#192634]`}>
        {HOURS.map((h) => (
          <div
            key={h}
            onClick={() => handleHour(h)}
            className={`${rowBase} ${
              selHour === h
                ? "bg-[#00E7FF]/10 text-[#00E7FF] font-medium"
                : "text-[#FEFCEB] hover:bg-white/5"
            }`}
          >
            {h}
          </div>
        ))}
      </div>

      {/* Minutes */}
      <div className={`${colBase} flex-1 border-r border-[#192634]`}>
        {MINUTES.map((m) => (
          <div
            key={m}
            onClick={() => handleMinute(m)}
            className={`${rowBase} ${
              selMinute === m
                ? "bg-[#00E7FF]/10 text-[#00E7FF] font-medium"
                : "text-[#FEFCEB] hover:bg-white/5"
            }`}
          >
            {String(m).padStart(2, "0")}
          </div>
        ))}
      </div>

      {/* AM/PM */}
      <div className={`${colBase} w-[64px]`}>
        {["AM", "PM"].map((p) => (
          <div
            key={p}
            onClick={() => handlePeriod(p)}
            className={`${rowBase} ${
              selPeriod === p
                ? "bg-[#00E7FF]/10 text-[#00E7FF] font-medium"
                : "text-[#FEFCEB] hover:bg-white/5"
            }`}
          >
            {p}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`w-full h-11 px-4 bg-app-surface border rounded-full flex items-center justify-between transition-colors ${
          isOpen ? "border-[#00E7FF]" : "border-transparent"
        }`}
      >
        <span className={`text-[15px] ${displayLabel ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
          {displayLabel || "Select time"}
        </span>
        {isOpen ? (
          <FiChevronUp className="w-4 h-4 text-[#FEFCEB] shrink-0" />
        ) : (
          <FiChevronDown className="w-4 h-4 text-[#FEFCEB] shrink-0" />
        )}
      </button>

      {createPortal(dropdown, document.body)}
    </div>
  );
};

export default TimePicker;
